import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Home, 
  Bath, 
  Bed, 
  Square, 
  MapPin, 
  Calendar,
  Phone,
  Mail,
  Filter,
  X,
  ChevronDown,
  Loader2,
  ExternalLink
} from "lucide-react";
import { PropertyContactModal } from "../components/PropertyContactModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPrice = (price) => {
  if (!price) return "Price TBD";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

const PropertyCard = ({ listing }) => {
  const [imageError, setImageError] = useState(false);
  
  // Placeholder image if no photos available
  const placeholderImage = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80";
  const mainImage = listing.photos?.[0]?.MediaURL || placeholderImage;

  return (
    <Link 
      to={`/listings/${listing.listingKey}`}
      className="bg-white shadow-lg overflow-hidden group hover:shadow-xl transition-shadow duration-300 block"
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={imageError ? placeholderImage : mainImage}
          alt={listing.address}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={() => setImageError(true)}
        />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 text-sm font-semibold ${
            listing.status === 'Active' 
              ? 'bg-green-500 text-white' 
              : listing.status === 'Pending'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-500 text-white'
          }`}>
            {listing.status || 'Unknown'}
          </span>
        </div>
        <div className="absolute bottom-4 left-4">
          <span className="bg-[#0F172A] text-white px-4 py-2 text-xl font-bold">
            {formatPrice(listing.price)}
          </span>
        </div>
        {/* View Details Indicator */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="bg-[#D4AF37] text-[#0F172A] px-3 py-1 text-sm font-semibold flex items-center gap-1">
            View Details
            <ExternalLink size={14} />
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-[#0F172A] mb-2 font-serif group-hover:text-[#D4AF37] transition-colors">
          {listing.address || 'Address Not Available'}
        </h3>
        <p className="text-[#475569] flex items-center gap-2 mb-4">
          <MapPin size={16} className="text-[#D4AF37]" />
          {listing.city}, {listing.state} {listing.zipCode}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-6 py-4 border-t border-b border-gray-100">
          {listing.beds && (
            <div className="flex items-center gap-2">
              <Bed size={18} className="text-[#D4AF37]" />
              <span className="text-[#0F172A] font-medium">{listing.beds} Beds</span>
            </div>
          )}
          {listing.baths && (
            <div className="flex items-center gap-2">
              <Bath size={18} className="text-[#D4AF37]" />
              <span className="text-[#0F172A] font-medium">{listing.baths} Baths</span>
            </div>
          )}
          {listing.sqft && (
            <div className="flex items-center gap-2">
              <Square size={18} className="text-[#D4AF37]" />
              <span className="text-[#0F172A] font-medium">{listing.sqft.toLocaleString()} Sqft</span>
            </div>
          )}
        </div>

        {/* Description */}
        {listing.description && (
          <p className="text-[#475569] text-sm mt-4 line-clamp-3">
            {listing.description}
          </p>
        )}

        {/* Agent Info */}
        {listing.coListingAgent?.firstName && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-[#94A3B8] mb-1">Listed by</p>
            <p className="text-[#0F172A] font-medium">
              {listing.coListingAgent.firstName} {listing.coListingAgent.lastName}
            </p>
          </div>
        )}

        {/* Year Built & Days on Market */}
        <div className="flex items-center gap-4 mt-4 text-sm text-[#94A3B8]">
          {listing.yearBuilt && (
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              Built {listing.yearBuilt}
            </span>
          )}
          {listing.daysOnMarket && (
            <span>{listing.daysOnMarket} days on market</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default function SearchListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: "",
    maxPrice: "",
    beds: "",
    city: ""
  });

  const fetchListings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append("limit", "20");
      
      if (filters.minPrice) params.append("min_price", filters.minPrice);
      if (filters.maxPrice) params.append("max_price", filters.maxPrice);
      if (filters.beds) params.append("beds", filters.beds);
      if (filters.city) params.append("city", filters.city);
      
      const response = await axios.get(`${API}/listings?${params.toString()}`);
      setListings(response.data.listings || []);
    } catch (err) {
      console.error("Error fetching listings:", err);
      setError("Unable to load listings at this time. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    fetchListings();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ minPrice: "", maxPrice: "", beds: "", city: "" });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F172A] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-serif">
            Search Listings
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl">
            Browse available properties in North Carolina. Find your next chapter home with our curated selection of listings.
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] hover:bg-[#0F172A] hover:text-white transition-colors text-[#0F172A] font-medium"
              >
                <Filter size={18} />
                Filters
                <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              
              <span className="text-[#94A3B8]">
                {listings.length} {listings.length === 1 ? 'property' : 'properties'} found
              </span>
            </div>

            {(filters.minPrice || filters.maxPrice || filters.beds || filters.city) && (
              <button
                onClick={clearFilters}
                className="text-[#D4AF37] hover:text-[#0F172A] text-sm font-medium flex items-center gap-1"
              >
                <X size={16} />
                Clear Filters
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">Min Price</label>
                <input
                  type="number"
                  name="minPrice"
                  value={filters.minPrice}
                  onChange={handleFilterChange}
                  placeholder="$0"
                  className="w-full p-2 border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">Max Price</label>
                <input
                  type="number"
                  name="maxPrice"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  placeholder="$1,000,000+"
                  className="w-full p-2 border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">Bedrooms</label>
                <select
                  name="beds"
                  value={filters.beds}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                >
                  <option value="">Any</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                  <option value="5">5+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1">City</label>
                <input
                  type="text"
                  name="city"
                  value={filters.city}
                  onChange={handleFilterChange}
                  placeholder="e.g., Raleigh"
                  className="w-full p-2 border border-gray-200 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                />
              </div>
              <div className="md:col-span-4">
                <button
                  onClick={applyFilters}
                  className="btn-primary"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Listings Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mb-4" />
            <p className="text-[#475569]">Loading listings...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <Home className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#0F172A] mb-2">Unable to Load Listings</h3>
            <p className="text-[#475569] mb-6">{error}</p>
            <button onClick={fetchListings} className="btn-primary">
              Try Again
            </button>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Home className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Listings Found</h3>
            <p className="text-[#475569] mb-6">
              No properties match your current search criteria. Try adjusting your filters or check back soon for new listings.
            </p>
            <button onClick={clearFilters} className="btn-outline">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {listings.map((listing) => (
              <PropertyCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </div>

      {/* Contact CTA */}
      <div className="bg-[#0F172A] py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-serif mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-white/70 mb-8">
            Let us help you find the perfect home for your next chapter. Contact us for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:919-879-5636" className="btn-primary">
              <Phone size={18} />
              Call (919) 879-5636
            </a>
            <button onClick={() => setShowContactModal(true)} className="btn-secondary">
              <Mail size={18} />
              Send a Message
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0F172A] border-t border-white/10 py-8 px-6 text-center">
        <p className="text-[#94A3B8] text-sm">
          © {new Date().getFullYear()} Next Chapter Moves NC. All rights reserved.
        </p>
      </div>

      {/* Contact Modal */}
      <PropertyContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)}
        property={null}
      />
    </div>
  );
}
